import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import DripRemovalModal from '@/components/DripRemovalModal';
import { Clock, Zap, Settings as SettingsIcon } from 'lucide-react';

/**
 * Shown once, the first time a member opens Module 2. Paced release is the intended
 * experience, so keeping the pacing is the primary choice; removing it is offered
 * quietly, and afterwards only lives in Settings.
 */
export const DripPacingOffer = ({ open, tier, onClose, onPurchased }) => {
  const [showCheckout, setShowCheckout] = useState(false);

  const dismiss = async () => {
    try {
      await axios.post(`${API}/user/drip-prompt/seen`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
    } catch {
      /* the offer simply reappears next visit if this fails */
    }
    onClose();
  };

  if (showCheckout) {
    return (
      <DripRemovalModal
        isOpen
        currentTier={tier}
        onClose={dismiss}
        onSuccess={() => { onPurchased?.(); dismiss(); }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={dismiss}>
      <DialogContent className="max-w-lg" data-testid="drip-pacing-offer">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Clock className="w-6 h-6 text-cyan-600" />
            Your program opens as you go
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            From here on, each folder opens as you finish the one before it. That pacing is
            deliberate — the work in these modules lands deeper when it has room to settle, and
            most people get far more from the program by moving through it this way.
          </p>
          <p className="text-sm text-gray-600">
            If you would rather set your own pace, there is a one-time option to open everything
            in your tier straight away. Entirely your choice, and it changes nothing about the
            content itself.
          </p>

          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={dismiss}
              className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700
                         hover:to-cyan-800 text-white"
              data-testid="drip-offer-keep-pacing-btn"
            >
              Keep the pacing
            </Button>
            <Button
              onClick={() => setShowCheckout(true)}
              variant="outline"
              className="w-full border-yellow-400 text-yellow-700 hover:bg-yellow-50"
              data-testid="drip-offer-remove-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Remove the drip feed instead
            </Button>
          </div>

          <p className="flex items-start gap-2 text-xs text-gray-500 pt-1">
            <SettingsIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span data-testid="drip-offer-settings-note">
              You will not be asked again. If you change your mind later, it is always waiting
              under <strong>Settings → Remove Drip Feed</strong>.
            </span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DripPacingOffer;
